import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Share2, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Helmet } from 'react-helmet-async';
import BoxSeatLogo from '../components/BoxSeatLogo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STADIUM_BG = "https://static.prod-images.emergentagent.com/jobs/9d09c8fc-6c12-438c-818d-2182801a5113/images/109fdd4fe55a21c3558a2d7e62a8e8dde2b0a13799111929723f6bfa5fad1d25.png";

export default function PublicMeetupPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchMeetup = async () => {
      try {
        const res = await axios.get(`${API}/meetups/share/${shareId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Meetup not found' : 'Failed to load meetup');
      } finally {
        setLoading(false);
      }
    };
    fetchMeetup();
  }, [shareId]);

  const shareUrl = `${window.location.origin}/m/${shareId}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: data?.meetup?.title, text: data?.meetup?.description, url: shareUrl });
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-bg text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold uppercase mb-4">{error}</h1>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-primary text-black font-bold uppercase rounded-md hover:bg-primary-hover transition-all">Go Home</button>
        </div>
      </div>
    );
  }

  const meetup = data.meetup;
  const creator = data.creator;
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-bg text-white">
      {meetup && (
        <Helmet>
          <title>{meetup.title} — Box Seat Meetup</title>
          <meta property="og:title" content={`${meetup.title} — Box Seat Meetup`} />
          <meta property="og:description" content={meetup.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={`${meetup.title} — Box Seat`} />
          <meta name="twitter:description" content={meetup.description} />
        </Helmet>
      )}
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
          <button
            data-testid="public-meetup-signin-btn"
            onClick={() => navigate('/login')}
            className="px-5 py-2 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber"
          >
            Sign In to RSVP
          </button>
        </div>
      </header>

      {/* Hero */}
      <div className="relative pt-16">
        <div className="absolute inset-0">
          <img src={STADIUM_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-bg" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          {/* Ticket-style card */}
          <div data-testid="public-meetup-card" className="bg-surface/90 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-amber-500/10">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-primary via-primary to-transparent" />

            <div className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Main content */}
                <div className="flex-1">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Fan Meetup</p>
                  <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight uppercase mb-4">{meetup.title}</h1>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-8">{meetup.description}</p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Calendar size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{formatDate(meetup.date)}</p>
                        <p className="text-sm text-zinc-500">at {meetup.time}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <MapPin size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{meetup.location}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Users size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{data.participant_count} attending</p>
                        {meetup.max_participants && <p className="text-sm text-zinc-500">{meetup.max_participants - data.participant_count} spots left</p>}
                      </div>
                    </div>
                  </div>

                  {creator && (
                    <p className="text-sm text-zinc-500">Organized by <span className="text-white font-semibold">{creator.full_name}</span></p>
                  )}

                  {meetup.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {meetup.categories.map((c) => <span key={c} className="px-3 py-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full font-semibold">{c}</span>)}
                    </div>
                  )}
                </div>

                {/* QR Code & Share */}
                <div className="flex flex-col items-center gap-6">
                  <div data-testid="meetup-qr-code" className="bg-white p-4 rounded-xl shadow-lg">
                    <QRCodeSVG value={shareUrl} size={160} />
                  </div>
                  <p className="text-xs text-zinc-500 text-center">Scan to view this meetup</p>

                  <div className="flex flex-col gap-2 w-full">
                    <button
                      data-testid="share-meetup-btn"
                      onClick={handleNativeShare}
                      className="w-full px-6 py-3 bg-white/5 border border-white/10 text-white font-bold uppercase text-sm rounded-md hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                    >
                      <Share2 size={16} /> Share
                    </button>
                    <button
                      data-testid="copy-link-btn"
                      onClick={handleCopy}
                      className="w-full px-6 py-3 bg-white/5 border border-white/10 text-white font-bold uppercase text-sm rounded-md hover:bg-white/10 transition-all"
                    >
                      {copied ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="border-t border-white/10 p-6 md:px-12 bg-black/30 flex items-center justify-between">
              <p className="text-sm text-zinc-400">Want to join this meetup?</p>
              <button
                data-testid="public-meetup-rsvp-btn"
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-primary text-black font-bold uppercase text-sm rounded-md hover:bg-primary-hover transition-all glow-amber"
              >
                Sign In to RSVP
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center">
          <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
          <p className="text-xs text-zinc-600 mt-2">Philadelphia Sports & Entertainment Platform</p>
        </div>
      </footer>
    </div>
  );
}
